import React, { useCallback, useMemo, useState } from 'react';
import { View, useWindowDimensions } from 'react-native';
import ToolCard, { ToolCardPlaceholder } from '@/components/Tools/ToolCard';
import useResponsiveness from '@/hooks/useResponsiveness';
import { getToolGridCardWidthPx, getToolGridColumnCount } from '@/components/Tools/toolGridLayout';

const ToolGrid = ({
  tools,
  isLoading = false,
  placeholderCount = 8,
  onToolPress,
  style,
  columns,
}) => {
  const { isXS } = useResponsiveness();
  const { width: windowWidth } = useWindowDimensions();
  const [measuredWidth, setMeasuredWidth] = useState(null);

  const gap = isXS ? 10 : 12;

  const onGridLayout = useCallback((e) => {
    const w = e?.nativeEvent?.layout?.width;
    if (typeof w === 'number' && w > 0) {
      setMeasuredWidth(Math.round(w));
    }
  }, []);

  const bodyWidth = measuredWidth ?? windowWidth;

  // `columns` lets callers (e.g. the Images/Videos/Music explore sections) force
  // a fixed column count so tool cards line up with the model grid; otherwise
  // we auto-fit based on available width.
  const columnCount = useMemo(
    () => (columns && columns > 0 ? columns : getToolGridColumnCount(bodyWidth, gap)),
    [columns, bodyWidth, gap],
  );

  const cardWidthPx = useMemo(
    () => getToolGridCardWidthPx(bodyWidth, columnCount, gap),
    [bodyWidth, columnCount, gap],
  );

  const gridStyle = useMemo(() => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap,
    width: '100%',
  }), [gap]);

  if (isLoading) {
    return (
      <View style={[gridStyle, style]} onLayout={onGridLayout}>
        {[...Array(placeholderCount)].map((_, index) => (
          <ToolCardPlaceholder key={index} measuredCardWidth={cardWidthPx} />
        ))}
      </View>
    );
  }

  return (
    <View style={[gridStyle, style]} onLayout={onGridLayout}>
      {Array.isArray(tools) && tools.map(tool => (
        <ToolCard
          key={tool.id || tool._id || tool.slug}
          tool={tool}
          onPress={onToolPress}
          measuredCardWidth={cardWidthPx}
        />
      ))}
    </View>
  );
};

export default React.memo(ToolGrid);
